import {Component, ContentChildren, Inject, OnInit, QueryList, ViewContainerRef} from '@angular/core';
import {AuthenticationService} from '@ngx-ivengi/authentication';
import {INotificationMenuItem} from '@ngx-ivengi/cleanzone/notification-menu/notification-menu-item.interface';
import {CrudAction, CrudInteractionService, Notification as CrudNotification} from '@ngx-ivengi/crud';
import {Notification, NotificationLevel, NotificationService} from '@ngx-ivengi/notification';
import {ToastsManager} from 'ng2-toastr';
import {Observable} from 'rxjs/Observable';
import {Channel} from './components/channel/channel.model';
import {AppConfig} from './components/config/app.config';
import {TranslateService} from '@ngx-translate/core';
import {MaintenanceService} from './components/maintenance/maintenance.service';
import {ProfileService} from './components/profile/profile.service';
import {Profile} from './components/profile/profile.model';
import {SharedService} from './components/shared/shared.service';
import {User} from './components/user/user.model';
import {LoginComponent} from './components/login/login.component';
import { Building } from './components/building/building.model';

export interface CustomNotificationRule {
  level?: NotificationLevel;
  action?: CrudAction | CrudAction[];
  entity?: any;
  httpCode?: number;
  errors?: {
    param: string;
    msg: string;
  }[];
  translateKey: string;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit {
  @ContentChildren(LoginComponent) children: QueryList<LoginComponent>;

  private unauthenticatedBodyClasses: string  = 'texture';

  public isAuthenticated: boolean = this.authService.isAuthenticated();
  public bodyClasses: string = (this.isAuthenticated) ? '' : this.unauthenticatedBodyClasses;
  public secureState: boolean;
  public appName: string = this.appConfig.name;
  public profile: Profile;
  public profileName: string;
  public maintenanceItems: Observable<INotificationMenuItem[]>;
  public maintenanceShowAll: Observable<string>;
  public hideLoaderForced = this.sharedService.hideLoaderForced;

  constructor(
    @Inject(AppConfig) private appConfig,
    private authService: AuthenticationService,
    private translate: TranslateService,
    private profileService: ProfileService,
    private interactionService: CrudInteractionService,
    private notificationService: NotificationService,
    private toastr: ToastsManager,
    private viewContainerRef: ViewContainerRef,
    private maintenanceService: MaintenanceService,
    private sharedService: SharedService
  ) {
    this.toastr.setRootViewContainerRef(viewContainerRef);
  }

  public ngOnInit(): void {

    this.authService.authenticationStatus().subscribe((isAuthenticated) => {
      const interval = setInterval(() => {
        const userBack = document.getElementById('userback_button_container');
        if (userBack) {
          clearInterval(interval);
          if (isAuthenticated) {
            if (this.authService.getAuthorizationScopesSync().includes('feedback:create')) {
              userBack.style.display = 'block';
            } else {
              userBack.style.display = 'none';
            }
          } else {
            userBack.style.display = 'none';
          }
        }
      }, 100);
    });

    this.maintenanceShowAll = this.translate.get('maintenance.headerNav.viewAll');

    this.notificationService.notification().subscribe((notification: Notification) => {
      if(notification instanceof CrudNotification) {

        // check for custom crud notification, or else, display the default notification
        if(this.customCrudNotificationMsg(notification) === false) {
          this.translate.get(`entity.${(<any>notification.getEntity()).name.toLowerCase()}`).subscribe((entity) => {
            this.translate.get(`notification.${notification.getLevel()}.${notification.getAction()}`, {entity: entity}).subscribe((message) => {
              switch(notification.getLevel()) {
                case NotificationLevel.SUCCESS: {

                  switch(notification.getAction()) {
                    case CrudAction.CREATE:
                    case CrudAction.UPDATE:
                    case CrudAction.DELETE: {
                      this.toastr[notification.getLevel()](message);
                    } break;
                  }

                } break;

                default: {
                  switch(notification.getHttpCode()) {
                    case 401: {
                      // do nothing, refresh token or redirect will already handle this
                    } break;

                    case 403: {
                      this.translate.get(`notification.unauthorized.${notification.getAction()}`, {entity: entity.toLowerCase()}).subscribe((unauthorizedMsg) => {
                        this.toastr[notification.getLevel()](unauthorizedMsg);
                      });
                    } break;

                    case 422: {
                      switch((<any>notification.getEntity()).name) {
                        case Channel.name: {
                          const fields = Object.keys(notification.getData().errors).map((property: string) => {
                            return this.translate.instant(`channel.form.error.properties.${property}`);
                          }).join(', ');

                          this.translate.get('channel.form.error.422', {fields: fields}).subscribe((translation: string) => {
                            this.toastr[notification.getLevel()](translation);
                          });
                        } break;

                        default: {
                          this.toastr[notification.getLevel()](message);
                        } break;
                      }
                    } break;

                    default: {
                      this.toastr[notification.getLevel()](message);
                    } break;
                  }
                }
              }
            });
          });
        }
      } else {
        this.translate.get(notification.getData()).subscribe((translation: string) => {
          if(translation !== notification.getData()) {
            this.toastr[notification.getLevel()](translation);
          }
        });
      }
    });

    this.authService.authenticationStatus().subscribe((response) => {
      this.isAuthenticated  = response;
      this.bodyClasses      = (response) ? '' : this.unauthenticatedBodyClasses;

      if(this.isAuthenticated === true) {
        this.loadProfile();
      }
    });

    if(this.authService.isAuthenticated()) {
      this.loadProfile();
    }
  }

  private customCrudNotificationMsg(notification: CrudNotification): boolean {
    const rules: CustomNotificationRule[] = [
      {
        level: NotificationLevel.ERROR,
        action: [CrudAction.CREATE, CrudAction.UPDATE],
        entity: User,
        httpCode: 422,
        errors: [
          {param: 'email', msg: 'already in use'}
        ],
        translateKey: 'notification.custom.error.user.emailInUse'
      },
      {
        level: NotificationLevel.ERROR,
        action: [CrudAction.CREATE, CrudAction.UPDATE],
        entity: User,
        httpCode: 422,
        errors: [
          {param: 'username', msg: 'already in use'}
        ],
        translateKey: 'notification.custom.error.user.usernameInUse'
      },
      {
        level: NotificationLevel.ERROR,
        action: [CrudAction.DELETE],
        entity: Building,
        httpCode: 422,
        errors: [
          {param: 'building', msg: 'building still linked'}
        ],
        translateKey: 'notification.custom.error.building.linked'
      }
    ];


    for(const rule of rules) {
      if(rule.level && rule.level !== notification.getLevel()) {
        continue;
      }

      if(rule.action) {
        rule.action = (rule.action instanceof Array) ? rule.action : [rule.action];
        if(rule.action.indexOf(notification.getAction()) === -1) {
          continue;
        }
      }

      if(rule.entity && rule.entity !== notification.getEntity()) {
        continue;
      }

      if(rule.httpCode && rule.httpCode !== notification.getHttpCode()) {
        continue;
      }

      if(rule.errors && notification.getData() && notification.getData().errors) {
        let specifiedErrorsFound = 0;
        for(let i = 0; i < rule.errors.length; i++) {
          for(const key in notification.getData().errors) {
            if(notification.getData().errors.hasOwnProperty(key)) {
              const error = notification.getData().errors[key];
              if(error.param === rule.errors[i].param && error.msg === rule.errors[i].msg) {
                specifiedErrorsFound++;
              }
            }
          }
        }

        if(specifiedErrorsFound !== rule.errors.length) {
          continue;
        }
      }

      // Still here? Show custom message!
      this.translate.get(rule.translateKey).subscribe((translation: string) => {
        this.toastr[notification.getLevel()](translation);
      });
      return true;
    }

    return false;
  }

  private loadProfile(): void {
    this.profileService.get().subscribe((profile: Profile) => {
      this.profile = profile;
      this.profileName  = `${profile.getFirstName()} ${profile.getLastName()}`;

      if(this.profile.isEmployee) {
        this.maintenanceItems = <any>this.maintenanceService.fetch();
        this.maintenanceService.getAllActive().subscribe();
      }
    });
  }
}



// WEBPACK FOOTER //
// /var/jenkins_home/workspace/brands-compart-portal-frontend/checkout/src/app/app.component.ts